import type { ApprovalWarning } from '../../api/onboarding.api.js';
import { CAN_APPROVE_EDD, holdsPermission } from '../../auth/permissions.js';

/**
 * When the back office offers Approve on a customer's KYC.
 *
 * DEV-112: a customer the nightly re-screen moved to "EDD required" without
 * opening an EDD case had no Approve anywhere — the queue offered it only for
 * PENDING_REVIEW / UNDER_REVIEW, and the customer's KYC tab had no decision at
 * all. The server still decides (readiness gate, open EDD case, PROHIBITED);
 * this only decides whether the button is offered.
 *
 * The statuses come in three spellings: the queue's case status
 * (PENDING_REVIEW, UNDER_REVIEW, EDD_REQUIRED), the Customer 360 status from
 * the KYC service (PENDING, EDD_REQUIRED), and the raw database status the
 * Customer 360 falls back to when the KYC service is unreachable
 * (UNDER_REVIEW, PENDING_EDD).
 *
 * Clearing "EDD required", and keeping it, also need the can_approve_edd
 * permission (CTO decision, 2026-10-01). The server reads it from the staff
 * record and adds four eyes; the back office reads the session's copy only to
 * decide what to offer, and says why when it offers nothing.
 */

/** Who is looking: the signed-in staff member's role and session permissions. */
export interface KycViewer {
  readonly role?: string | undefined;
  readonly permissions?: readonly string[] | undefined;
}

const APPROVABLE_STATUSES: ReadonlySet<string> = new Set([
  'PENDING_REVIEW',
  'UNDER_REVIEW',
  'PENDING',
  'EDD_REQUIRED',
  'PENDING_EDD',
]);

/** The server's REASON_SCHEMA minimum; a shorter reason is refused with a 400. */
export const MIN_REASON_LENGTH = 4;

/** The roles the server lets decide a KYC case (`/admin/kyc/*`). */
const DECIDING_ROLES: ReadonlySet<string> = new Set(['admin', 'compliance']);

function decides(viewer: KycViewer | null | undefined): boolean {
  return viewer?.role !== undefined && DECIDING_ROLES.has(viewer.role);
}

/** True for "EDD required" in any of its spellings. */
export function isEddRequired(status: string): boolean {
  return status === 'EDD_REQUIRED' || status === 'PENDING_EDD';
}

export function mayOfferKycApproval(viewer: KycViewer | null | undefined, status: string): boolean {
  if (!decides(viewer) || !APPROVABLE_STATUSES.has(status)) return false;
  return !isEddRequired(status) || holdsPermission(viewer, CAN_APPROVE_EDD);
}

/**
 * Why a deciding officer is offered nothing on "EDD required": the missing
 * permission, in plain words. Null whenever there is nothing to explain.
 */
export function eddPermissionNote(viewer: KycViewer | null | undefined, status: string): string | null {
  if (!decides(viewer) || !isEddRequired(status) || holdsPermission(viewer, CAN_APPROVE_EDD)) return null;
  return (
    'Clearing or keeping "EDD required" needs the EDD-approval permission. ' +
    'An administrator can grant it on the Admin Users page. ' +
    // The session carries permissions as they were at sign-in (15-minute token).
    'If it was granted just now, sign out and back in.'
  );
}

/**
 * Whether to offer "Keep restriction" beside Approve: the officer's other
 * recorded outcome for a customer in EDD required (DEV-112). The server
 * refuses it for any other status, while an EDD case is open, and without
 * can_approve_edd.
 */
export function mayOfferKeepEddRestriction(viewer: KycViewer | null | undefined, status: string): boolean {
  return decides(viewer) && isEddRequired(status) && holdsPermission(viewer, CAN_APPROVE_EDD);
}

/**
 * The screening warnings on an approval readiness (DEV-114). Missing screening
 * WARNS at approval and never blocks; every approve dialog lists these above
 * the reason field.
 */
export function screeningWarningsOf(
  readiness: { warnings?: ApprovalWarning[] | undefined } | null | undefined,
): ApprovalWarning[] {
  return (readiness?.warnings ?? []).filter((w) => w.code === 'SCREENING');
}

/** One profile, one line: "Director Jane Doe: no sanctions screening result". */
export function screeningWarningLine(warning: ApprovalWarning): string {
  return `${warning.profile}: ${warning.detail}`;
}

/** The headline above the per-profile list. */
export function screeningWarningHeadline(count: number): string {
  return (
    `Sanctions screening has no usable result for ${String(count)} ${count === 1 ? 'profile' : 'profiles'}. ` +
    'You can still approve; the approval will be recorded as made without a screening result.'
  );
}

/**
 * Possible matches apart from missing results (compliance review, DEV-114).
 * A possible match is a finding, not a gap: it is never shown under "no
 * usable result", and gets its own, louder alert in the approve dialogs.
 */
export function splitScreeningWarnings(
  readiness: { warnings?: ApprovalWarning[] | undefined } | null | undefined,
): { matches: ApprovalWarning[]; missing: ApprovalWarning[] } {
  const warnings = screeningWarningsOf(readiness);
  return {
    matches: warnings.filter((w) => w.kind === 'MATCH_NOT_REVIEWED'),
    missing: warnings.filter((w) => w.kind !== 'MATCH_NOT_REVIEWED'),
  };
}

/**
 * The review page's readiness line when the file is approvable but screening
 * is short: a possible match named first, then missing results. Null when
 * screening is in order — "All checks complete" is then true.
 */
export function screeningReviewBanner(
  readiness: { warnings?: ApprovalWarning[] | undefined } | null | undefined,
): string | null {
  const { matches, missing } = splitScreeningWarnings(readiness);
  const profiles = (n: number): string => `${String(n)} ${n === 1 ? 'profile' : 'profiles'}`;
  const parts = [
    ...(matches.length === 0
      ? []
      : [
          `screening found a possible sanctions match for ${profiles(matches.length)} that ` +
            `${matches.length === 1 ? 'has' : 'have'} not been reviewed`,
        ]),
    ...(missing.length === 0
      ? []
      : [
          `${matches.length === 0 ? 'sanctions screening ' : ''}has no usable result for ${profiles(missing.length)}`,
        ]),
  ];
  if (parts.length === 0) return null;
  return `This application can be approved, but ${parts.join(', and ')} — see the approve dialog.`;
}

/** The headline above the unreviewed possible matches. */
export function screeningMatchHeadline(count: number): string {
  const one = count === 1;
  return (
    `Screening found a possible sanctions match for ${String(count)} ${one ? 'profile' : 'profiles'} ` +
    `that ${one ? 'has' : 'have'} not been reviewed. ` +
    'Review it before approving; if you approve, the approval will be recorded as made over an unreviewed possible match.'
  );
}
